<script setup lang="ts">
  import { PhoneNumber } from '@borg/types';
  import { MpInput, MpSelect } from '@borg/ui';
  import { stringIncludes } from '@borg/utils';
  import phone from './phone.json';

  interface Option {
    value: string;
    label: string;
    code: string;
    countryName: string;
    countryCode: string;
    locale: string;
  }

  const props = defineProps<{
    modelValue?: PhoneNumber | null;
    countryCodeLabel?: string;
    numberLabel: string;
    error?: string;
    disabled?: boolean;
  }>();

  const emit = defineEmits<{
    (event: 'update:modelValue', modelValue: PhoneNumber): void;
  }>();

  const { t } = useI18n();

  const phoneCodes = phone.map<Option>((entry) => ({
    value: `${entry.dialCode}__${entry.code}`,
    label: `${entry.emoji} ${entry.code} (+${entry.dialCode})`,
    code: entry.dialCode,
    countryName: entry.name,
    countryCode: entry.dialCode,
    locale: entry.code,
  }));

  const fallBackPhone =
    phoneCodes.find((code) => code.locale.toLowerCase() === t('ISO_3166_ALPHA_2').toLowerCase()) || phoneCodes[0];

  function getSelectedOption() {
    if (props.modelValue?.countryCode) {
      return (
        phoneCodes.find((code) => code.code === props.modelValue?.countryCode) || fallBackPhone
      );
    } else {
      return fallBackPhone;
    }
  }

  function updateCountryCode(option: Option) {
    emit('update:modelValue', {
      countryCode: option.countryCode,
      number: props.modelValue?.number || '',
    });
  }

  function updateNumber(number?: string) {
    emit('update:modelValue', {
      countryCode: props.modelValue?.countryCode || fallBackPhone.countryCode,
      number: number || '',
    });
  }

  function getOptions(query: string | undefined) {
    const queryString = query ?? '';
    return phoneCodes.filter(
      (phone) =>
        stringIncludes(phone.countryName, queryString) ||
        stringIncludes(phone.code, queryString) ||
        stringIncludes(phone.value, queryString),
    );
  }
</script>

<template>
  <div class="phone">
    <MpSelect
      :model-value="getSelectedOption()"
      :options="getOptions"
      searchable
      :unselectable="false"
      dropdown-class="country-code"
      :max-menu-height="250"
      :disabled="disabled"
      :label="countryCodeLabel ? countryCodeLabel : $t('COUNTRY_CODE_LABEL')"
      @update:model-value="updateCountryCode"
    />
    <MpInput
      :model-value="modelValue?.number || ''"
      :error="error"
      :disabled="disabled"
      :label="numberLabel"
      @update:model-value="updateNumber"
    />
  </div>
</template>

<style lang="scss">
  .country-code .options__item > .mp-text {
    font: var(--mp-body2-regular);
    font-family: 'NotoColorEmojiLimited', 'source sans pro';
  }
</style>

<style scoped lang="scss">
  @font-face {
    font-family: NotoColorEmojiLimited;
    unicode-range: U+1F1E6-1F1FF;
    src: url(https://raw.githack.com/googlefonts/noto-emoji/main/fonts/NotoColorEmoji.ttf);
  }
  .phone {
    display: grid;
    grid-template-columns: 9rem 5fr;
    gap: var(--mp-space-30);
    :deep(.selectable__text) {
      font: var(--mp-body2-regular);
      font-family: 'NotoColorEmojiLimited', 'source sans pro';
    }
  }
</style>
